﻿import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getRoleTabs, postSelfImproveBeacon, getMyAgentDashboards } from '@/api';
import {
  LayoutDashboard, MessageSquare, Bot, Radio,
  BookOpen, Clock, Settings, LogOut, ChevronLeft, ChevronRight,
  Wrench, ShieldCheck, Plug, Workflow, Puzzle, Activity, Sun, Moon, Menu, X,
  KeyRound, ScrollText, Search, Cpu, MessagesSquare, ShieldAlert,
  Monitor, Lightbulb, BarChart3,
} from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import CommandPalette, { PaletteItem } from '@/components/CommandPalette';
import DeepSeekBalance from '@/components/DeepSeekBalance';

// Sidebar groups â€” flat per-tab visibility is still governed by ROLE_TABS below.
const NAV_GROUPS: { label: string; items: { tab: string; icon: ReactNode; label: string; keywords?: string }[] }[] = [
  {
    label: 'Overview',
    items: [
      { tab: 'dashboard', icon: <LayoutDashboard size={20} />, label: 'Dashboard', keywords: 'home overview status' },
      { tab: 'bizdash', icon: <BarChart3 size={20} />, label: 'Business Dashboard', keywords: 'founder business kpi sales revenue target ageing customers suppliers afr' },
      { tab: 'watchdog', icon: <Activity size={20} />, label: 'Watchdog', keywords: 'health monitor restart uptime' },
    ],
  },
  {
    label: 'Agents',
    items: [
      { tab: 'chat', icon: <MessageSquare size={20} />, label: 'Chat', keywords: 'super chat conversation' },
      { tab: 'dept-chat', icon: <MessagesSquare size={20} />, label: 'Dept Chat', keywords: 'department chat' },
      { tab: 'agents', icon: <Bot size={20} />, label: 'Agents', keywords: 'bots departments tools memory soul' },
    ],
  },
  {
    label: 'Automation',
    items: [
      { tab: 'builder', icon: <Puzzle size={20} />, label: 'Visual Builder', keywords: 'flow graph build' },
      { tab: 'workflows', icon: <Workflow size={20} />, label: 'Workflows', keywords: 'approval process pipeline hr finance' },
      { tab: 'selfimprove', icon: <Lightbulb size={20} />, label: 'Self-Improve', keywords: 'telemetry errors proposals diagnose improve selfimprove' },
      { tab: 'browser', icon: <Monitor size={20} />, label: 'Browser', keywords: 'browse automate page viewport screencast' },
      { tab: 'schedules', icon: <Clock size={20} />, label: 'Schedules', keywords: 'cron jobs timer recurring' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { tab: 'channels', icon: <Radio size={20} />, label: 'Channels', keywords: 'telegram whatsapp gmail slack email' },
      { tab: 'governance', icon: <ShieldCheck size={20} />, label: 'Governance', keywords: 'policy compliance rules' },
      { tab: 'guardrails', icon: <ShieldAlert size={20} />, label: 'Guardrails', keywords: 'safety allow deny risk' },
      { tab: 'dash-access', icon: <KeyRound size={20} />, label: 'Dashboard Access', keywords: 'roles tabs features visibility sidebar permissions' },
      { tab: 'rbac', icon: <KeyRound size={20} />, label: 'RBAC', keywords: 'roles permissions access users' },
      { tab: 'tools', icon: <Wrench size={20} />, label: 'Tools', keywords: 'skills tool library skills' },
      { tab: 'mcp', icon: <Plug size={20} />, label: 'MCP', keywords: 'servers integrations google sheets' },
      { tab: 'bridge', icon: <Monitor size={20} />, label: 'Bridge', keywords: 'desktop approvals permission allow deny monitor' },
    ],
  },
  {
    label: 'System',
    items: [
      { tab: 'deus', icon: <Monitor size={20} />, label: 'DEUS Viewer', keywords: 'browser session distilled takeover replay eval pages' },
      { tab: 'logs', icon: <ScrollText size={20} />, label: 'Logs', keywords: 'gateway tail errors debug trace' },
      { tab: 'settings', icon: <Settings size={20} />, label: 'Settings', keywords: 'config profile model llm providers' },
    ],
  },
];

// Tabs visible per dashboard role — now controlled by the super admin
// via the Dashboard Access page (config dashboard.roleTabs). Defaults apply
// until a super admin saves a custom mapping.
const DEFAULT_ROLE_TABS: Record<string, string[]> = {
  super_admin: NAV_GROUPS.flatMap((g) => g.items.map((i) => i.tab)),
  admin: ['dashboard', 'chat', 'agents', 'channels', 'workflows'],
  manager: ['dashboard', 'chat', 'agents'],
  chat_only: ['chat'],
  user: [],
};

const ALL_PALETTE_ITEMS: PaletteItem[] = NAV_GROUPS.flatMap((g) =>
  g.items.map((i) => ({ tab: i.tab, label: i.label, group: g.label, keywords: i.keywords }))
);

export default function Layout({
  activeTab,
  onTabChange,
  children,
}: {
  activeTab: string;
  onTabChange: (tab: string) => void;
  children: ReactNode;
}) {
  const { user, logout } = useAuth();
  const role = user?.role || 'user';
  const ROLE_LABELS: Record<string, string> = {
    super_admin: 'Admin', admin: 'Admin', manager: 'Manager', user: 'User', viewer: 'Viewer', chat_only: 'Chat Only',
  };
  const roleLabel = ROLE_LABELS[role] || role;
  const initials = (user?.name || user?.email || '?').split(/\s+/).map((w) => w[0] || '').slice(0, 2).join('').toUpperCase();
  const [effectiveTabs, setEffectiveTabs] = useState<string[]>(DEFAULT_ROLE_TABS[role] || []);
  const [hasAgentDashboard, setHasAgentDashboard] = useState(false);
  useEffect(() => {
    getRoleTabs()
      .then((r) => setEffectiveTabs(r?.effectiveTabs || r?.roleTabs?.[role] || []))
      .catch(() => {});
    getMyAgentDashboards()
      .then((r) => setHasAgentDashboard((r?.dashboards || []).length > 0))
      .catch(() => {});
  }, [role]);

  // Self-improve telemetry beacon: passive page-view tracking (best-effort)
  useEffect(() => {
    try {
      postSelfImproveBeacon({ page: activeTab, action: 'page_view' }).catch(() => {});
    } catch {
      /* beacon is best-effort */
    }
  }, [activeTab]);
  const allowedTabs = hasAgentDashboard && !effectiveTabs.includes('bizdash') ? [...effectiveTabs, 'bizdash'] : effectiveTabs;
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  // Ctrl/Cmd+K â†’ command palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((p) => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => allowedTabs.includes(i.tab)),
  })).filter((g) => g.items.length > 0);

  const activeLabel = NAV_GROUPS.flatMap((g) => g.items).find((i) => i.tab === activeTab)?.label || 'Admin';

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-slate-900">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 flex flex-col bg-white dark:bg-slate-800 transition-all duration-200 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static w-56 ${collapsed ? 'lg:w-16' : 'lg:w-56'}`}>
        {/* Logo */}
        <div className="h-14 flex items-center px-4 border-b border-gray-100 dark:border-slate-700">
          <span className={`text-lg font-bold text-indigo-600 tracking-tight ${collapsed ? 'lg:hidden' : ''} dark:text-indigo-400`}>
            Climate Circle<span className="text-gray-400 dark:text-slate-500">/admin</span>
          </span>
          <span className={`text-lg font-bold text-indigo-600 mx-auto ${collapsed ? 'hidden lg:block' : 'hidden'} dark:text-indigo-400`}>CC</span>
          <button onClick={() => setMobileOpen(false)} className="lg:hidden ml-auto p-1.5 text-gray-500 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 dark:text-slate-400">
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-2 overflow-y-auto space-y-0.5">
          {visibleGroups.map((group) => (
            <div key={group.label} className="mb-3">
              {!collapsed && (
                <div className="px-4 mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  {group.label}
                </div>
              )}
              {group.items.map((item) => (
                <button
                  key={item.tab}
                  onClick={() => { onTabChange(item.tab); setMobileOpen(false); }}
                  title={collapsed ? item.label : undefined}
                  aria-current={activeTab === item.tab ? 'page' : undefined}
                  className={`group w-full relative flex items-center gap-3 px-3 py-2 text-sm mx-2 rounded-lg transition-[background-color,color,transform] duration-150 active:scale-[.985] ${
                    activeTab === item.tab
                      ? 'bg-indigo-50 text-indigo-700 font-semibold dark:bg-indigo-500/15 dark:text-indigo-200'
                      : 'text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700/70 hover:text-gray-900 dark:hover:text-slate-100'
                  } ${collapsed ? 'justify-center lg:w-auto' : ''}`}
                >
                  {activeTab === item.tab && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-indigo-600 dark:bg-indigo-400" />
                  )}
                  <span className="shrink-0 transition-transform duration-150 group-hover:scale-110">{item.icon}</span>
                  <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center h-10 border-t border-gray-100 dark:border-slate-700 text-gray-400 dark:text-slate-500 hover:text-indigo-600 transition dark:hover:text-indigo-400"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 shrink-0 z-20 flex items-center justify-between px-4 lg:px-6 bg-white/85 dark:bg-slate-800/85 backdrop-blur-md border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400 min-w-0">
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 -ml-2 text-gray-500 hover:text-gray-700 dark:hover:text-slate-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 dark:text-slate-400" title="Menu">
              <Menu size={18} />
            </button>
            <span className="font-semibold text-gray-800 dark:text-white truncate dark:text-slate-200">{activeLabel}</span>
          </div>
          <div className="flex items-center gap-3">
            <DeepSeekBalance />
            <button
              onClick={() => setPaletteOpen(true)}
              className="hidden sm:flex items-center gap-2 text-xs text-gray-400 dark:text-slate-500 border border-gray-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 hover:border-indigo-300 hover:text-indigo-500 transition dark:hover:border-indigo-500/40"
              title="Search (Ctrl+K)"
            >
              <Search size={13} />
              <span>Searchâ€¦</span>
              <kbd className="text-[10px] font-medium border border-gray-200 dark:border-slate-600 rounded px-1 py-px">Ctrl K</kbd>
            </button>
            <button onClick={() => setPaletteOpen(true)} className="sm:hidden p-2 text-gray-500 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 dark:text-slate-400" title="Search">
              <Search size={16} />
            </button>
            <button
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="flex items-center justify-center w-8 h-8 text-gray-500 dark:text-slate-400 hover:text-indigo-600 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition dark:hover:text-indigo-400"
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {user && (
              <div className="hidden md:flex items-center gap-2 pl-3 border-l border-gray-200 dark:border-slate-700" title={`${user.name || user.email} · ${roleLabel}`}>
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-[10px] font-bold text-white shadow-sm shrink-0">
                  {initials}
                </div>
                <div className="leading-tight min-w-0 max-w-[140px]">
                  <div className="text-xs font-semibold text-gray-800 dark:text-white truncate dark:text-slate-200">{user.name || user.email}</div>
                  <div className="text-[10px] text-gray-400 dark:text-slate-500">{roleLabel}</div>
                </div>
              </div>
            )}
            <button
              onClick={logout}
              className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        <div key={activeTab} className="flex-1 min-h-0 overflow-y-auto animate-fade-up">{children}</div>
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={ALL_PALETTE_ITEMS} onNavigate={onTabChange} />
    </div>
  );
}
